import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Sidebar.css"; // <--- External CSS for custom styles/utilities (not provided)
import toast from "react-hot-toast";

import { useAuth } from "../../contexts/AuthContext";

// Imported Sub-Components
import SupportIcons from "./SupportIcons";
import NavSection from "./NavSection";
import SupportSection from "./SupportSection";
import SidebarFooter from "./SidebarFooter";
import SupportModal from "./SupportModal";

const Sidebar = ({
  currentView: currentViewProp,
  navigateTo: navigateToProp,
  sidebarOpen,
  setSidebarOpen,
  userData,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModal, setActiveModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoggedIn, logout } = useAuth();

  const QuotationsIcon = ({ className }) => (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 14h6m-6 4h6M9 10h6M7 3h10a2 2 0 012 2v16l-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 012-2z"
      />
    </svg>
  );

  const navItems = [
    {
      id: "home",
      icon: "fas fa-home",
      label: "Home",
      path: "/",
      hideOnMobile: true,
    },
    // {
    //   id: "personal",
    //   icon: "far fa-user-circle",
    //   label: "Profile",
    //   path: "/personal",
    // },
    {
      id: "address",
      icon: "fas fa-map-marker-alt",
      label: "Address",
      path: "/address",
    },
    {
      id: "quotations",
      icon: QuotationsIcon,
      label: "Quotations",
      path: "/quotations",
      hideOnMobile: true,
    },

    { id: "kyc", icon: "fas fa-user-shield", label: "KYC", path: "/kyc" },
    {
      id: "orders",
      icon: "fas fa-box-open",
      label: "Orders",
      path: "/orders",
      hideOnMobile: true,
    },
    { id: "bank", icon: "fas fa-university", label: "Bank", path: "/bank" },
    { id: "bonus", icon: "fas fa-dollar", label: "Bonus", path: "/bonus" },
    {
      id: "payments",
      icon: "fas fa-wallet",
      label: "Payments",
      path: "/transactions",
      hideOnMobile: true,
    },
  ];

  const supportItems = [
    {
      id: "ticket",
      icon: "Ticket",
      label: "Support Center",
      path: "/support",
      description: "Create & view tickets",
    },
  ];

  const handleSupportClick = (item) => {
    if (item.action === "external") {
      window.open(item.url, "_blank");
    } else if (item.action === "modal") {
      setActiveModal(item.id);
      setIsModalOpen(true);
    } else if (item.path) {
      if (typeof navigateToProp === "function") {
        navigateToProp(item.id);
      } else {
        navigate(item.path);
      }
    }
    if (setSidebarOpen) setSidebarOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveModal(null);
  };

  const isActiveById = (itemId, path) => {
    if (typeof currentViewProp === "string") {
      return currentViewProp === itemId;
    }
    if (itemId === "orders") {
      return location.pathname.startsWith("/orders");
    }
    return location.pathname === path;
  };

  const goTo = (itemId, path) => {
    // Navigation logic
    if (typeof navigateToProp === "function") {
      navigateToProp(itemId);
    } else {
      navigate(path);
    }
    if (typeof setSidebarOpen === "function") {
      setSidebarOpen(false);
    }
  };

  const handleLoginClick = () => {
    if (isLoggedIn) {
      logout();
      toast.success("Logged out successfully");
      navigate("/login");
    } else {
      navigate("/login");
    }

    if (typeof setSidebarOpen === "function") {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <aside
        className={`fixed left-0 top-16 md:top-20 h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)] w-48 md:w-52 lg:w-60 bg-white border-r border-gray-100 transform transition-transform duration-300 z-40 flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={
          !sidebarOpen &&
          typeof window !== "undefined" &&
          window.innerWidth < 768
        }
      >
        <div className="flex-1 overflow-y-auto hide-scrollbar py-3 md:py-2 lg:py-5 px-2 md:px-2 lg:px-4">
          {/* Main Navigation Section */}
          <NavSection
            navItems={navItems}
            isActiveById={isActiveById}
            goTo={goTo}
            SupportIcons={SupportIcons}
            supportItems={supportItems}
          />

          {/* Support/Links Section */}
          <SupportSection
            supportItems={supportItems}
            isActiveById={isActiveById}
            handleSupportClick={handleSupportClick}
            SupportIcons={SupportIcons}
          />
        </div>

        <div className="flex-shrink-0 border-t border-gray-100">
          <SidebarFooter // <--- Always visible at the bottom of the sidebar
            userData={userData}
            handleLoginClick={handleLoginClick}
            isLoggedIn={isLoggedIn}
            navigate={navigate}
          />
        </div>
      </aside>

      {isModalOpen && (
        <SupportModal // Modal for support/quotation
          type={activeModal}
          onClose={closeModal}
          userData={userData}
        />
      )}
    </>
  );
};

export default Sidebar;
